import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";

import { ProductAddToCart } from "../styles/product";

import {
    AddProductToCart,
    UpdateProduct,
} from "../utils/productInCart";

import { ADD_ITEM } from "../features/productInCartSlice";
import QuantityCounter from "./QuantityCounter";
import { useNavigate } from "react-router-dom";

const ProductButtons = ({
    product,
    selectedVariations,
    isOutOfStock = false,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const loggedUser = useSelector(
        (state) => state.user.logedUser
    );

    const cart = useSelector(
        (state) => state.cart.items
    );


    /*
     * הווריאציה שנבחרה:
     * צבע או מידה.
     */
    const selectedOption =
        selectedVariations?.Color ??
        selectedVariations?.Size ??
        null;


    /*
     * מזהה הווריאציה שנבחרה.
     */
    const selectedOptionId =
        selectedOption?.id ??
        selectedOption?.Id ??
        null;


    /*
     * אובייקט הווריאציה שנשמר בסל.
     */
    const productOption = selectedOption
        ? {
            id:
                selectedOption.id ??
                selectedOption.Id,

            optionType:
                selectedOption.optionType ??
                selectedOption.OptionType,

            value:
                selectedOption.value ??
                selectedOption.Value,

            extraPrice:
                selectedOption.extraPrice ??
                selectedOption.ExtraPrice ??
                null,

            quantity:
                selectedOption.quantity ??
                selectedOption.Quantity ??
                null,

            image:
                selectedOption.image ??
                selectedOption.Image ??
                null,

            isInStock:
                selectedOption.isInStock ??
                selectedOption.IsInStock ??
                true,
        }
        : null;


    /*
     * השוואת מזהי וריאציה.
     * תומך גם במזהה מספרי וגם במזהה שמגיע כמחרוזת.
     */
    const isSameOption = (
        firstOptionId,
        secondOptionId
    ) => {
        const first =
            firstOptionId ?? null;

        const second =
            secondOptionId ?? null;

        if (first === null && second === null) {
            return true;
        }

        if (first === null || second === null) {
            return false;
        }

        return Number(first) === Number(second);
    };


    /*
     * חיפוש המוצר בסל לפי:
     * מזהה מוצר + מזהה וריאציה.
     */
    const existProduct = cart?.find(
        (item) =>
            Number(item?.productId) ===
            Number(product?.id) &&
            isSameOption(
                item?.productOptionId,
                selectedOptionId
            )
    );


    /*
     * אם המוצר כבר בסל:
     * מתחילים מהכמות הקיימת.
     *
     * אם הוא לא בסל:
     * מתחילים מכמות 1.
     */
    const productAmount =
        Number(existProduct?.amount) || 1;

    const [count, setCount] =
        useState(productAmount);


    /*
     * בכל מעבר למוצר אחר או לווריאציה אחרת,
     * מעדכנים את הכמות המוצגת.
     *
     * אין כאן בדיקה של Quantity.
     * המלאי נקבע רק לפי IsInStock.
     */
    useEffect(() => {
        if (isOutOfStock) {
            setCount(0);
            return;
        }

        setCount(
            Number(existProduct?.amount) || 1
        );
    }, [
        existProduct?.amount,
        product?.id,
        selectedOptionId,
        isOutOfStock,
    ]);


    /*
     * שינוי הכמות.
     *
     * אין הגבלת כמות לפי המלאי.
     * רק מונעים מספר שלילי.
     */
    const handleQuantityChange = (
        newValue
    ) => {
        if (isOutOfStock) {
            setCount(0);
            return;
        }

        let value = Number(newValue);

        if (Number.isNaN(value)) {
            return;
        }

        if (value < 0) {
            value = 0;
        }

        setCount(value);
    };


    /*
     * הודעה כאשר המוצר או הווריאציה
     * מוגדרים כלא במלאי.
     */
    const showOutOfStockMessage = () => {
        Swal.fire({
            icon: "info",
            title: "המוצר אינו במלאי",
            text: selectedOption
                ? "הווריאציה שבחרת אינה זמינה כרגע."
                : "המוצר אינו זמין כרגע לרכישה.",
            confirmButtonText: "אישור",
            customClass: {
                popup: "custom-swal",
            },
        });
    };


    /*
     * הוספה או עדכון של מוצר בסל.
     */
    const addToCartWithNotification =
        async () => {
            /*
             * לא מאפשרים הוספה כאשר
             * IsInStock הוא false.
             */
            if (isOutOfStock) {
                showOutOfStockMessage();
                return;
            }
            if (existProduct) {
                navigate("/cart");
                return;
            }


            /*
             * חייבים לבחור כמות גדולה מ־0.
             */
            if (count <= 0) {
                Swal.fire({
                    icon: "warning",
                    title: "יש לבחור כמות",
                    confirmButtonText: "אישור",
                    customClass: {
                        popup: "custom-swal",
                    },
                });

                return;
            }


            /*
             * הנתונים שנשמרים ב־Redux.
             */
            const productInCart = {
                amount: count,

                customerId:
                    loggedUser?.id ?? null,

                productId:
                    product?.id ?? null,

                productOptionId:
                    selectedOptionId ?? null,

                productOption,

                product,
            };


            /*
             * הנתונים שנשלחים לשרת.
             */
            const dtoToServer = {
                amount: count,

                customerId:
                    loggedUser?.id ?? null,

                productId:
                    product?.id ?? null,

                productOptionId:
                    selectedOptionId ?? null,
            };


            /*
             * משתמש לא מחובר:
             * שומרים ב־Redux וב־localStorage.
             */
            if (!loggedUser) {
                dispatch(
                    ADD_ITEM({
                        ...productInCart,

                        id:
                            existProduct?.id ??
                            `${product.id}-${selectedOptionId ?? "no-option"}`,
                    })
                );


                Swal.fire({
                    position: "center",
                    icon: "success",
                    title: existProduct
                        ? "כמות המוצר עודכנה"
                        : "המוצר נוסף לסל",
                    showConfirmButton: false,
                    customClass: {
                        popup: "custom-swal",
                    },
                    timer: 1000,
                });

                return;
            }


            try {
                /*
                 * משתמש מחובר:
                 * אם המוצר עם הווריאציה הזאת
                 * עדיין לא נמצא בסל – מוסיפים למסד.
                 */
                if (!existProduct) {
                    const response =
                        await AddProductToCart({
                            ...dtoToServer,
                            product: null,
                        });


                    if (
                        response.status === 200 ||
                        response.status === 201
                    ) {
                        dispatch(
                            ADD_ITEM({
                                ...productInCart,
                                id: response.data.id,
                            })
                        );


                        Swal.fire({
                            position: "center",
                            icon: "success",
                            title:
                                "המוצר נוסף בהצלחה לסל",
                            showConfirmButton: false,
                            customClass: {
                                popup: "custom-swal",
                            },
                            timer: 1000,
                        });
                    }

                    return;
                }


                /*
                 * אם המוצר עם אותה וריאציה
                 * כבר נמצא בסל – מעדכנים כמות.
                 */
                const response =
                    await UpdateProduct(
                        existProduct.id,
                        productInCart
                    );


                if (
                    response.status === 200 ||
                    response.status === 204
                ) {
                    dispatch(
                        ADD_ITEM({
                            ...productInCart,
                            id: existProduct.id,
                        })
                    );


                    Swal.fire({
                        position: "center",
                        icon: "success",
                        title:
                            "כמות המוצר עודכנה בהצלחה",
                        showConfirmButton: false,
                        customClass: {
                            popup: "custom-swal",
                        },
                        timer: 1000,
                    });
                }
            } catch (error) {
                console.error(
                    "שגיאה בהוספת או בעדכון המוצר בסל:",
                    error
                );


                Swal.fire({
                    icon: "error",
                    title:
                        "לא הצלחנו לשמור את המוצר",
                    text:
                        "אירעה שגיאה. נסי שוב בעוד כמה רגעים.",
                    confirmButtonText: "אישור",
                    customClass: {
                        popup: "custom-swal",
                    },
                });
            }
        };


    /*
     * הטקסט שמופיע בכפתור.
     */
    const buttonText =
        isOutOfStock
            ? "לא במלאי"
            : existProduct
                ? "עדכן כמות"
                : "הוסף לסל";


    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mt: 1,
            }}
        >
            {/*
       * מציגים את בחירת הכמות כל עוד
       * IsInStock אינו false.
       *
       * אין max ואין בדיקה מול Quantity.
       */}
            {!existProduct && !isOutOfStock && (
                <QuantityCounter
                    value={count}
                    onChange={handleQuantityChange}
                    productName={product?.name}
                />
            )}


            <ProductAddToCart
                type="button"
                variant="contained"
                onClick={
                    addToCartWithNotification
                }
                disabled={isOutOfStock}
            >
                {buttonText}
            </ProductAddToCart>
        </Box>
    );
};


export default ProductButtons;